import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { ThemeToggle } from "../sirius/ThemeProvider";
import { LanguageToggle, useT } from "../i18n";
import { DaggryMark } from "../components/DaggryMark";

/**
 * /account/support — user-facing ticket centre.
 * - List of the current user's tickets (newest activity first)
 * - Click into a thread to view all messages + reply inline
 * - "New ticket" form
 *
 * The matching admin view lives inside AmexImport.jsx (SupportTab).
 */
const CATEGORIES = [
  { key: "general",  nb: "Generelt",      en: "General" },
  { key: "billing",  nb: "Fakturering",   en: "Billing" },
  { key: "account",  nb: "Konto",         en: "Account" },
  { key: "bug",      nb: "Feil i appen",  en: "Bug report" },
  { key: "feature",  nb: "Funksjonsønske", en: "Feature request" },
  { key: "other",    nb: "Annet",          en: "Other" },
];

const STATUS_LABEL = {
  open:     { nb: "Åpen",     en: "Open",     dot: "var(--srs-sun)" },
  resolved: { nb: "Løst",     en: "Resolved", dot: "var(--srs-good)" },
  closed:   { nb: "Lukket",   en: "Closed",   dot: "var(--srs-ink-faint)" },
};

const fmtDate = (iso, lang) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(lang === "en" ? "en-GB" : "nb-NO", {
      day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

export default function SupportPage() {
  const { token, apiFetch } = useAuth();
  const { lang } = useT();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const focusedId = params.get("id") || null;

  const [tickets, setTickets] = useState(null);
  const [activeTicket, setActiveTicket] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => { if (!token) navigate("/login?next=/account/support"); }, [token, navigate]);

  const loadList = useCallback(async () => {
    try {
      const data = await apiFetch("/api/support/tickets");
      setTickets(data.items || []);
    } catch (e) { setErr(e?.message || "Kunne ikke hente saker"); }
  }, [apiFetch]);

  const loadOne = useCallback(async (id) => {
    if (!id) { setActiveTicket(null); return; }
    try {
      const data = await apiFetch(`/api/support/tickets/${id}`);
      setActiveTicket(data.ticket);
    } catch (e) { setErr(e?.message || "Kunne ikke hente sak"); }
  }, [apiFetch]);

  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => { loadOne(focusedId); }, [focusedId, loadOne]);

  const t = (nb, en) => (lang === "en" ? en : nb);

  return (
    <div className="srs" style={{ minHeight: "100vh", display: "flex", flexDirection: "column" }} data-testid="support-root">
      {/* Nav */}
      <nav style={{ position: "sticky", top: 0, zIndex: 30, background: "color-mix(in srgb, var(--srs-bg) 88%, transparent)", backdropFilter: "blur(14px)", borderBottom: "1px solid var(--srs-line)" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", padding: "16px 28px", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 24 }}>
          <Link to="/" style={{ display: "flex", alignItems: "center", gap: 12, textDecoration: "none", color: "var(--srs-ink)" }}>
            <DaggryMark size={28} />
            <span className="srs-display" style={{ fontSize: 22, fontWeight: 500 }}>EuroPilot</span>
          </Link>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <LanguageToggle />
            <ThemeToggle />
            <Link to="/account" style={{ color: "var(--srs-ink-dim)", textDecoration: "none", fontSize: 13, fontWeight: 500 }} data-testid="back-to-account">← {t("Konto", "Account")}</Link>
          </div>
        </div>
      </nav>

      <main style={{ flex: 1, maxWidth: 900, width: "100%", margin: "0 auto", padding: "40px 28px 80px" }}>
        <div className="srs-mono" style={{ fontSize: 10, letterSpacing: "0.3em", color: "var(--srs-ink-dim)", marginBottom: 12 }}>// {t("KUNDESTØTTE", "SUPPORT")}</div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, marginBottom: 28, flexWrap: "wrap" }}>
          <h1 className="srs-display" style={{ fontSize: 40, fontWeight: 500, letterSpacing: "-0.02em", margin: 0, lineHeight: 1.05 }}>
            {activeTicket ? activeTicket.subject : t("Saker og samtaler", "Tickets & conversations")}
          </h1>
          {!activeTicket && (
            <button
              onClick={() => setShowNew(true)}
              data-testid="new-ticket-btn"
              style={{ background: "var(--srs-sun)", color: "#fff", border: "none", padding: "11px 20px", borderRadius: 999, fontSize: 13, fontWeight: 600, cursor: "pointer", boxShadow: "var(--srs-shadow-cta)" }}>
              + {t("Ny sak", "New ticket")}
            </button>
          )}
        </div>

        {err && <div style={{ padding: "12px 16px", background: "color-mix(in srgb, var(--srs-warn) 15%, transparent)", border: "1px solid var(--srs-warn)", borderRadius: 10, marginBottom: 16, color: "var(--srs-warn)" }} data-testid="support-err">{err}</div>}

        {!activeTicket && (
          <TicketList items={tickets} t={t} lang={lang} onOpen={(id) => setParams({ id })} />
        )}

        {activeTicket && (
          <TicketThread ticket={activeTicket} t={t} lang={lang}
                        onClose={() => setParams({})}
                        onReply={async (body) => {
                          await apiFetch(`/api/support/tickets/${activeTicket.id}/messages`, {
                            method: "POST",
                            headers: { "Content-Type": "application/json" },
                            body: JSON.stringify({ body }),
                          });
                          await loadOne(activeTicket.id);
                        }} />
        )}
      </main>

      {showNew && (
        <NewTicketModal
          t={t} lang={lang}
          onClose={() => setShowNew(false)}
          onCreate={async (payload) => {
            const data = await apiFetch("/api/support/tickets", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(payload),
            });
            setShowNew(false);
            await loadList();
            if (data?.ticket?.id) setParams({ id: data.ticket.id });
          }}
        />
      )}

      <footer style={{ borderTop: "1px solid var(--srs-line)", padding: "24px 28px", textAlign: "center", fontSize: 12, color: "var(--srs-ink-faint)" }}>
        EuroPilot · Stavanger · <a href="mailto:hei@europilot.no" style={{ color: "var(--srs-sun)", textDecoration: "none" }}>hei@europilot.no</a>
      </footer>
    </div>
  );
}

function TicketList({ items, t, lang, onOpen }) {
  if (items === null) return <div style={{ color: "var(--srs-ink-dim)", fontSize: 14 }}>{t("Laster …", "Loading …")}</div>;
  if (items.length === 0) return (
    <div style={{ padding: "60px 24px", textAlign: "center", color: "var(--srs-ink-dim)", border: "1px dashed var(--srs-line)", borderRadius: 16 }} data-testid="support-empty">
      <div style={{ fontSize: 14, marginBottom: 6 }}>{t("Ingen saker enda.", "No tickets yet.")}</div>
      <div style={{ fontSize: 12, color: "var(--srs-ink-faint)" }}>{t("Trykk \"Ny sak\" for å sende en henvendelse.", "Hit \"New ticket\" to open a conversation.")}</div>
    </div>
  );
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }} data-testid="support-ticket-list">
      {items.map((it) => {
        const status = STATUS_LABEL[it.status] || STATUS_LABEL.open;
        const cat = CATEGORIES.find((c) => c.key === it.category) || CATEGORIES[0];
        return (
          <button key={it.id} onClick={() => onOpen(it.id)}
                  data-testid={`ticket-row-${it.id}`}
                  style={{ textAlign: "left", background: "var(--srs-surface)", border: "1px solid var(--srs-line)", borderRadius: 14, padding: "16px 18px", cursor: "pointer", display: "grid", gridTemplateColumns: "1fr auto", gap: 12, alignItems: "center", transition: "border-color 0.18s ease" }}
                  onMouseEnter={(e) => e.currentTarget.style.borderColor = "var(--srs-sun)"}
                  onMouseLeave={(e) => e.currentTarget.style.borderColor = "var(--srs-line)"}>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                <span style={{ width: 8, height: 8, borderRadius: "50%", background: status.dot }} />
                <span className="srs-mono" style={{ fontSize: 10, letterSpacing: "0.2em", color: "var(--srs-ink-dim)" }}>{t(cat.nb, cat.en).toUpperCase()}</span>
                <span style={{ fontSize: 11, color: "var(--srs-ink-faint)" }}>·</span>
                <span style={{ fontSize: 11, color: "var(--srs-ink-faint)" }}>{fmtDate(it.last_message_at, lang)}</span>
              </div>
              <div style={{ fontSize: 15, fontWeight: 600, color: "var(--srs-ink)", lineHeight: 1.3 }}>{it.subject}</div>
              <div style={{ fontSize: 12, color: "var(--srs-ink-dim)", marginTop: 4 }}>
                {it.message_count} {t("meldinger", "messages")} · {t(status.nb, status.en)}
                {it.last_message_by === "admin" && it.status !== "closed" && (
                  <span style={{ marginLeft: 8, padding: "2px 8px", background: "color-mix(in srgb, var(--srs-sun) 18%, transparent)", color: "var(--srs-sand-deep)", borderRadius: 999, fontSize: 10, fontWeight: 600, letterSpacing: "0.05em" }}>{t("NYTT SVAR", "NEW REPLY")}</span>
                )}
              </div>
            </div>
            <div style={{ color: "var(--srs-ink-faint)", fontSize: 18 }}>→</div>
          </button>
        );
      })}
    </div>
  );
}

function TicketThread({ ticket, t, lang, onClose, onReply }) {
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [localErr, setLocalErr] = useState("");
  const status = STATUS_LABEL[ticket.status] || STATUS_LABEL.open;
  const closed = ticket.status === "closed";

  const send = async () => {
    if (!draft.trim() || busy) return;
    setBusy(true); setLocalErr("");
    try { await onReply(draft.trim()); setDraft(""); }
    catch (e) { setLocalErr(e?.message || "Kunne ikke sende"); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="support-thread">
      <button onClick={onClose} data-testid="thread-back" style={{ background: "none", border: "none", color: "var(--srs-ink-dim)", fontSize: 13, cursor: "pointer", marginBottom: 16, padding: 0 }}>← {t("Tilbake til saker", "Back to tickets")}</button>

      <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 12, color: "var(--srs-ink-dim)", marginBottom: 24 }}>
        <span style={{ width: 8, height: 8, borderRadius: "50%", background: status.dot }} />
        <span>{t(status.nb, status.en)}</span>
        <span style={{ color: "var(--srs-ink-faint)" }}>·</span>
        <span>{t("Åpnet", "Opened")} {fmtDate(ticket.created_at, lang)}</span>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 14, marginBottom: 28 }}>
        {(ticket.messages || []).map((m) => (
          <div key={m.id} data-testid={`msg-${m.id}`}
               style={{
                 alignSelf: m.author_role === "admin" ? "flex-start" : "flex-end",
                 maxWidth: "78%",
                 background: m.author_role === "admin" ? "var(--srs-sand)" : "var(--srs-surface)",
                 border: m.author_role === "admin" ? "1px solid color-mix(in srgb, var(--srs-sun) 30%, transparent)" : "1px solid var(--srs-line)",
                 borderRadius: 14,
                 padding: "14px 16px",
               }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: m.author_role === "admin" ? "var(--srs-sand-deep)" : "var(--srs-ink-dim)" }}>
                {m.author_role === "admin" ? "EuroPilot" : t("Deg", "You")}
              </span>
              <span style={{ fontSize: 11, color: "var(--srs-ink-faint)" }}>{fmtDate(m.created_at, lang)}</span>
            </div>
            <div style={{ fontSize: 14, lineHeight: 1.55, whiteSpace: "pre-wrap", color: "var(--srs-ink)" }}>{m.body}</div>
          </div>
        ))}
      </div>

      {closed ? (
        <div style={{ padding: 16, textAlign: "center", border: "1px dashed var(--srs-line)", borderRadius: 12, color: "var(--srs-ink-dim)", fontSize: 13 }}>
          {t("Denne saken er lukket. Opprett en ny sak hvis du trenger mer hjelp.", "This ticket is closed. Open a new ticket if you need more help.")}
        </div>
      ) : (
        <div style={{ background: "var(--srs-surface)", border: "1px solid var(--srs-line)", borderRadius: 14, padding: 12 }}>
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={t("Skriv et svar …", "Write a reply …")}
            data-testid="reply-textarea"
            rows={4}
            style={{ width: "100%", border: "none", outline: "none", resize: "vertical", fontFamily: "inherit", fontSize: 14, lineHeight: 1.55, color: "var(--srs-ink)", background: "transparent" }}
          />
          {localErr && <div style={{ color: "var(--srs-warn)", fontSize: 12, marginTop: 4 }}>{localErr}</div>}
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
            <button onClick={send} disabled={!draft.trim() || busy}
                    data-testid="reply-send"
                    style={{ background: "var(--srs-ink)", color: "var(--srs-bg)", border: "none", padding: "10px 20px", borderRadius: 999, fontSize: 13, fontWeight: 600, cursor: draft.trim() && !busy ? "pointer" : "not-allowed", opacity: draft.trim() && !busy ? 1 : 0.5 }}>
              {busy ? t("Sender …", "Sending …") : t("Send svar", "Send reply")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function NewTicketModal({ t, onClose, onCreate, lang }) {
  const [subject, setSubject] = useState("");
  const [category, setCategory] = useState("general");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [localErr, setLocalErr] = useState("");

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!subject.trim() || !body.trim() || busy) return;
    setBusy(true); setLocalErr("");
    try { await onCreate({ subject: subject.trim(), category, body: body.trim() }); }
    catch (err) { setLocalErr(err?.message || "Kunne ikke opprette sak"); setBusy(false); }
  };

  return (
    <div onClick={onClose} data-testid="new-ticket-modal"
         style={{ position: "fixed", inset: 0, background: "rgba(11,36,64,0.55)", backdropFilter: "blur(8px)", display: "grid", placeItems: "center", padding: 24, zIndex: 50 }}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit}
            style={{ width: "100%", maxWidth: 560, background: "var(--srs-surface)", border: "1px solid var(--srs-line)", borderRadius: 20, padding: "28px 28px 24px", boxShadow: "var(--srs-shadow-float)" }}>
        <div className="srs-mono" style={{ fontSize: 10, letterSpacing: "0.3em", color: "var(--srs-sun)", marginBottom: 8, fontWeight: 600 }}>// {t("NY HENVENDELSE", "NEW TICKET")}</div>
        <h2 className="srs-display" style={{ fontSize: 26, fontWeight: 500, margin: "0 0 20px 0", color: "var(--srs-ink)" }}>{t("Hva kan vi hjelpe med?", "How can we help?")}</h2>

        <label style={{ display: "block", fontSize: 11, letterSpacing: "0.2em", color: "var(--srs-ink-dim)", marginBottom: 6, fontWeight: 600 }}>{t("EMNE", "SUBJECT")}</label>
        <input type="text" value={subject} onChange={(e) => setSubject(e.target.value)} required maxLength={160}
               data-testid="new-ticket-subject"
               style={{ width: "100%", padding: "10px 14px", border: "1px solid var(--srs-line)", borderRadius: 10, fontSize: 14, color: "var(--srs-ink)", background: "var(--srs-bg)", marginBottom: 16 }} />

        <label style={{ display: "block", fontSize: 11, letterSpacing: "0.2em", color: "var(--srs-ink-dim)", marginBottom: 6, fontWeight: 600 }}>{t("KATEGORI", "CATEGORY")}</label>
        <select value={category} onChange={(e) => setCategory(e.target.value)}
                data-testid="new-ticket-category"
                style={{ width: "100%", padding: "10px 14px", border: "1px solid var(--srs-line)", borderRadius: 10, fontSize: 14, color: "var(--srs-ink)", background: "var(--srs-bg)", marginBottom: 16 }}>
          {CATEGORIES.map((c) => <option key={c.key} value={c.key}>{t(c.nb, c.en)}</option>)}
        </select>

        <label style={{ display: "block", fontSize: 11, letterSpacing: "0.2em", color: "var(--srs-ink-dim)", marginBottom: 6, fontWeight: 600 }}>{t("MELDING", "MESSAGE")}</label>
        <textarea value={body} onChange={(e) => setBody(e.target.value)} required rows={6} maxLength={5000}
                  data-testid="new-ticket-body"
                  style={{ width: "100%", padding: "12px 14px", border: "1px solid var(--srs-line)", borderRadius: 10, fontSize: 14, color: "var(--srs-ink)", background: "var(--srs-bg)", resize: "vertical", lineHeight: 1.55 }} />

        {localErr && <div style={{ color: "var(--srs-warn)", fontSize: 12, marginTop: 8 }}>{localErr}</div>}

        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 18 }}>
          <button type="button" onClick={onClose} style={{ background: "none", border: "1px solid var(--srs-line)", color: "var(--srs-ink-dim)", padding: "10px 18px", borderRadius: 999, fontSize: 13, fontWeight: 500, cursor: "pointer" }}>{t("Avbryt", "Cancel")}</button>
          <button type="submit" disabled={!subject.trim() || !body.trim() || busy}
                  data-testid="new-ticket-submit"
                  style={{ background: "var(--srs-sun)", color: "#fff", border: "none", padding: "10px 22px", borderRadius: 999, fontSize: 13, fontWeight: 600, cursor: (subject.trim() && body.trim() && !busy) ? "pointer" : "not-allowed", opacity: (subject.trim() && body.trim() && !busy) ? 1 : 0.5, boxShadow: "var(--srs-shadow-cta)" }}>
            {busy ? t("Sender …", "Sending …") : t("Send henvendelse", "Send ticket")}
          </button>
        </div>
      </form>
    </div>
  );
}
